import { DOMParser, DOMSerializer } from '@tiptap/pm/model';

import { sanitizeHtml } from '../RichTextEditor/sanitizeHtml';

/**
 * doc <-> sections mapping for the unified letter editor (LTR-08).
 *
 * The backend keeps per-section LetterSection rows as the source of truth (so
 * per-section regenerate and the WeasyPrint render are untouched). The frontend
 * composes those rows into ONE ProseMirror doc for a single continuous editor,
 * and splits/splices manual edits back into the corresponding rows via the
 * existing per-section PATCH.
 *
 * A `sectionBoundary` atom node (frontend-only, see SectionBoundary.js) marks
 * where each section's body starts. Boundaries are NEVER serialized to stored
 * content: split excludes them and sanitizeHtml() strips `<div>` regardless.
 */

/**
 * Join each section as an invisible boundary marker immediately followed by its
 * content_html body, in the given order (caller pre-sorts by sort_order).
 * @param {Array<{id:string, section_key:string, content_html?:string}>} sections
 * @returns {string}
 */
export function composeSectionsHtml(sections) {
  return (sections || [])
    .map((s) => {
      const boundary =
        `<div data-section-boundary data-section-id="${s.id}"` +
        ` data-section-key="${s.section_key}"></div>`;
      return boundary + (s.content_html || '');
    })
    .join('');
}

function bucketsFromDoc(doc) {
  const buckets = [];
  let current = null;
  doc.forEach((node) => {
    if (node.type.name === 'sectionBoundary') {
      current = {
        sectionId: node.attrs.sectionId,
        sectionKey: node.attrs.sectionKey,
        nodes: [],
      };
      buckets.push(current);
    } else if (current) {
      // Nodes before the first boundary (there should be none) are ignored.
      current.nodes.push(node);
    }
  });
  return buckets;
}

/**
 * Walk the editor doc and return one entry per section in document order:
 * `{ sectionId, sectionKey, html, json }`. `html` is the sanitizeHtml() of ONLY
 * that section's body nodes (boundaries excluded); `json` is the key-only
 * ProseMirror JSON body (no PHI — VariableChip stores only its variable key).
 * An empty section body yields `html === ''`.
 * @param {import('@tiptap/core').Editor} editor
 */
export function splitDocToSections(editor) {
  const { doc, schema } = editor.state;
  const serializer = DOMSerializer.fromSchema(schema);
  return bucketsFromDoc(doc).map((bucket) => {
    let html = '';
    if (bucket.nodes.length) {
      const container = document.createElement('div');
      bucket.nodes.forEach((node) => {
        container.appendChild(serializer.serializeNode(node));
      });
      html = sanitizeHtml(container.innerHTML);
    }
    return {
      sectionId: bucket.sectionId,
      sectionKey: bucket.sectionKey,
      html,
      json: { type: 'doc', content: bucket.nodes.map((node) => node.toJSON()) },
    };
  });
}

/**
 * Replace ONLY the node range between the boundary for `sectionId` and the next
 * boundary (or doc end) with `newHtml` parsed into the schema. Other sections'
 * nodes are byte-identical before/after. An empty `newHtml` clears that section
 * (its split html becomes ''). Returns false if the boundary isn't found.
 * @param {import('@tiptap/core').Editor} editor
 * @param {string} sectionId
 * @param {string} newHtml
 */
export function spliceSectionInDoc(editor, sectionId, newHtml) {
  const { state } = editor;
  const { doc, schema } = state;

  const tops = [];
  doc.forEach((node, offset) => tops.push({ node, offset }));

  const idx = tops.findIndex(
    (t) => t.node.type.name === 'sectionBoundary' && t.node.attrs.sectionId === sectionId
  );
  if (idx === -1) return false;

  // Body range: right after this boundary, up to the next boundary (or doc end).
  const start = tops[idx].offset + tops[idx].node.nodeSize;
  let end = doc.content.size;
  for (let j = idx + 1; j < tops.length; j += 1) {
    if (tops[j].node.type.name === 'sectionBoundary') {
      end = tops[j].offset;
      break;
    }
  }

  const sanitized = sanitizeHtml(newHtml || '');
  let tr;
  if (!sanitized) {
    // Empty body: delete the whole region so the section has ZERO content nodes
    // (split -> ''). ProseMirror's DOMParser would otherwise fill a mandatory
    // empty <p> for the block+ schema, which is not the same as "no content".
    tr = state.tr.delete(start, end);
  } else {
    const container = document.createElement('div');
    container.innerHTML = sanitized;
    const parsed = DOMParser.fromSchema(schema).parse(container);
    tr = state.tr.replaceWith(start, end, parsed.content);
  }
  editor.view.dispatch(tr);
  return true;
}
